<template>
    <ui-modal>
        <template #header>
            <h3 v-text="title" />
        </template>

        <page-preloader
            v-if="isLoading"
            text="Trwa łączenie worków..."
            min-height="10rem"
        />

        <div v-else-if="showWarning" class="text-center p-3">
            <a-icon
                icon="WarningFull"
                width="2.5rem"
                height="2.5rem"
                class="text-warning mb-3"
            />
            <h5 v-text="$options.warningText" class="font-weight-normal text-orange" />
            <h5 v-html="$options.warningDetailsText" class="font-weight-normal" />
        </div>

        <div v-else>
            <div v-text="$options.warningBoxText" class="a-infoBar a-infoBar--warning text-sm" />

            <sub-section-header title="Worki" class="font-weight-medium mt-4 mb-3 mb-md-0" />

            <div class="c-table c-table--merge-bags">
                <div class="c-thead">
                    <div v-text="'Docelowy'" class="c-td" />
                    <div v-text="'Frakcja'" class="c-td" />
                    <div v-text="'Status'" class="c-td" />
                    <div class="c-td">
                        Wartość worka
                        <a-icon
                            v-tooltip="$options.bagValueTooltip"
                            icon="Info"
                            width="16"
                            height="16"
                            style="position: relative; top: 2px"
                        />
                    </div>
                    <div v-text="'Akcje'" class="c-td" />
                </div>

                <div class="c-tbody">
                    <div v-for="bag in selectedPackages" class="c-row" :key="bag.package_id">
                        <merge-bag-row
                            :bag="bag"
                            :expanded-id="expandedId"
                            :target-id="targetId"
                            @setExpandedId="val => expandedId = val"
                            @setTargetId="val => targetId = val"
                        />
                    </div>
                </div>
            </div>
        </div>

        <template #footer v-if="!isLoading">
            <the-btn
                :text="showWarning ? 'Wróć': 'Zamknij'"
                class="btn-outline-custom"
                @click="onReject"
            />
            <the-btn
                v-tooltip="canMergeTooltip"
                text="Połącz worki"
                class="btn-secondary"
                :disabled="!canMerge"
                @click="onConfirm"
            />
        </template>
    </ui-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
    warningBoxText: `Zaznacz worek docelowy, do którego przesypiesz opakowania
                z worków do opróżnienia. Pamiętaj, że możesz łączyć frakcje
                "Butelki plastikowe" oraz "Puszki metalowe", jeśli Twój sklep ma ustawioną
                zbiórkę "Mix". Opróżnione worki automatycznie zamykają się i znikają z listy.`,

    warningText: 'Upewnij się, czy wszystkie opakowania, które zamierzasz przesypać, zmieszczą się do worka wskazanego jako docelowy.',

    warningDetailsText: 'Jeśli nie mieszczą się, wróć do poprzedniego kroku i zmodyfikuj listę worków, które chcesz połączyć.<br/>Jeśli opakowania pomieszczą się w worku docelowym, użyj przycisku “Połącz worki”. Tej operacji nie da się odwrócić.',

    bagValueTooltip: 'Na wartość worka składa się liczba sztuk przemnożona przez opłatę operatora',

    noTargetTooltip: 'Zaznacz worek, do którego przesypiesz opakowania z worków do opróżnienia',

    notEnoughBagsTooltip: 'Do prawidłowego obsłużenia procesu łączenia worków niezbędne są co najmniej <strong>dwa worki</strong> ze statusem <strong>"OTWARTY”</strong>',

    data: () => ({
        isLoading: false,
        showWarning: false,
        expandedId: null,
        targetId: null
    }),

    computed: {
        ...mapGetters('drsBags', ['selectedPackages']),

        title() {
            return `Łączenie worków (${this.selectedPackages.length})`
        },

        canConfirm() {
            return this.selectedPackages.length > 1
        },

        canMerge() {
            return this.targetId && this.canConfirm
        },

        canMergeTooltip() {
            if (this.canMerge) return null

            return (!this.canConfirm && this.$options.notEnoughBagsTooltip) ||
                (!this.targetId && this.$options.noTargetTooltip) ||
                null
        }
    },

    methods: {
        ...mapActions('drsBags', ['mergePackages', 'loadPackages', 'resetMode']),

        onReject() {
            if (this.showWarning) {
                this.showWarning = false
                return
            }

            this.$emit('close-modal')
        },

        async onConfirm() {
            if (!this.showWarning) {
                this.showWarning = true
                return
            }

            if (!this.canMerge) return

            this.isLoading = true
            try {
                // merge bags
                await this.mergeBags()

                // reset merge mode
                this.resetMode()

                // update packages
                await this.loadPackages()
            } finally {
                this.isLoading = false
                this.$emit('close-modal')
            }
        },

        async mergeBags() {
            const toApiFormat = pkg => ({
                mcuid: pkg.mcuid,
                fractions: pkg.fractions.map(f => f.id.toString())
            })

            const targetId = this.targetId
            const target = this.selectedPackages.find(p => p.package_id === targetId)
            const merge = this.selectedPackages.filter(p => p.package_id !== targetId)

            await this.mergePackages({
                targetPackages: toApiFormat(target),
                mergePackages: merge.map(pkg => toApiFormat(pkg))
            })
        }
    }
}
</script>

<style lang="scss">
.c-table--merge-bags {
    --gap: 1rem;
    .c-thead {
        align-items: flex-start;
        gap: var(--gap);
    }

    .c-tr {
        gap: var(--gap);
    }

    > .c-thead > .c-td, .merge-bags-row > .c-td {
        text-align: start;

        .typo-label {
            display: none;
        }

        &:nth-child(1) {
            --width: 5rem;
            max-width: var(--width);
        }
        &:nth-child(2) {
            --width: 15%;
        }
        &:nth-child(3) {
            --width: 10%;
            .status {
                max-width: 7rem;
            }
        }
        &:nth-child(4) {
            --width: 10%;
        }
        &:nth-child(5) {
            --width: 16rem;
            max-width: var(--width);
        }
    }
}
</style>